import axios from "axios";
import { jwtDecode } from "jwt-decode";
import toast from "react-hot-toast";

// function getSubdomain() {
//   const hostname = window.location.hostname;
//   const parts = hostname.split(".");
//   if (parts.length >= 2) {
//     return parts[0];
//   }
//   return null;
// }

// const API_HOST = process.env.REACT_APP_BASE_URL;
// const subdomain = getSubdomain();
// let AtriumApi = axios.create({
//   baseURL: API_HOST + "/api",
//   headers: {
//     "Content-Type": "application/json",
//     ...(subdomain && { "x-church-slug": subdomain }),
//   },
//   retries: 0,
// });

export function getSubdomain() {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  if (parts.length >= 2) {
    return parts[0];
  }
  // For local development, use environment variable or atriumdemo as default
  // Also check for IP addresses (they have dots but are numeric)
  if (hostname === 'localhost' || hostname === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return process.env.REACT_APP_CHURCH_SLUG || 'atriumdemo';
  }
  return null;
}

const subdomain = getSubdomain();

const API_HOST = process.env.REACT_APP_BASE_URL;

const AtriumApi = axios.create({
  baseURL: API_HOST + "/api",
  headers: {
    "Content-Type": "application/json",
    ...(subdomain && { "x-church-slug": subdomain }),
  },
});

AtriumApi.interceptors.request.use(
  (config) => {
    try {
      const subdomain = getSubdomain();
      if (subdomain) {
        config.headers["x-church-slug"] = subdomain;
      }
      const token = localStorage.auth && JSON.parse(localStorage.auth);
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }

      // For FormData, remove Content-Type so axios sets it with correct boundary
      if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
      }

      return config;
    } catch (error) {
      return config;
    }
  },
  (error) => Promise.reject(error)
);
// AtriumApi.interceptors.request.use(
//   function (config) {
//     let token;
//     try {
//       const dynamicSubdomain = getSubdomain();
//       if (dynamicSubdomain) {
//         config.headers["x-church-slug"] = dynamicSubdomain;
//       }
//       token = localStorage.auth && JSON.parse(localStorage.auth);
//       if (token) config.headers["Authorization"] = `Bearer ${token}`;
//     } finally {
//       return config;
//     }
//   },
//   function (error) {
//     return Promise.reject(error);
//   }
// );

// Shared refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const onRefreshFailed = (err) => {
  refreshSubscribers.forEach((cb) => cb(null, err));
  refreshSubscribers = [];
};

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

AtriumApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status || error.status;

    if (status === 401) {
      // Before doing anything, check if we have a valid (non-expired) token.
      // A stale in-flight request can return 401 even though the user just logged in
      // with a fresh token — don't kill the new session for a stale request.
      try {
        const raw = localStorage.getItem("auth");
        if (raw) {
          const currentToken = JSON.parse(raw);
          const decoded = jwtDecode(currentToken);
          if (decoded.exp && decoded.exp * 1000 > Date.now()) {
            // Token is still valid — this 401 is from a stale request, just throw
            throw error;
          }
        }
      } catch (e) {
        if (e === error) throw e; // re-throw the original axios error from above
        // Token couldn't be parsed/decoded — proceed with refresh/logout
      }

      const originalConfig = error.config;

      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token, err) => {
            if (err || !token) return reject(error);
            originalConfig.headers["Authorization"] = `Bearer ${token}`;
            resolve(AtriumApi(originalConfig));
          });
        });
      }

      isRefreshing = true;

      try {
        const slug = getSubdomain();
        const response = await axios.post(
          "/v1/refresh-token",
          {},
          {
            baseURL: API_HOST + "/api",
            withCredentials: true,
            headers: {
              ...(slug && { "x-church-slug": slug }),
            },
          }
        );
        if (response?.data?.token) {
          const user = jwtDecode(response.data.token);
          localStorage.setItem("user", JSON.stringify(user));
          localStorage.setItem("auth", JSON.stringify(response.data.token));
          originalConfig.headers["Authorization"] = `Bearer ${response.data.token}`;
          onRefreshed(response.data.token);
          isRefreshing = false;
          return await AtriumApi(originalConfig);
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        onRefreshFailed(refreshError);
      }

      isRefreshing = false;

      // Refresh failed - logout gracefully, preserve churchSlug for login page
      localStorage.removeItem("auth");
      localStorage.removeItem("user");
      localStorage.removeItem("atriumAuthToken");
      localStorage.removeItem("atriumUser");

      if (!window.location.pathname.startsWith("/auth/")) {
        toast.error("Session has expired. Please log in again.");
        window.location.href = "/auth/signin";
      }
      throw error;
    } else if (status === 422) {
      return error.response;
    }
    throw error;
  }
);

const AXIOS_STATUS_MESSAGE = /^Request failed with status code \d+$/;

const firstErrorMessage = (errors) => {
  if (!Array.isArray(errors)) return null;
  for (const entry of errors) {
    if (typeof entry === "string" && entry) return entry;
    const message = entry?.message || entry?.msg;
    if (typeof message === "string" && message) return message;
  }
  return null;
};

/**
 * User-facing message for a failed request: the server's message when it sent one,
 * otherwise `fallback`. Also accepts the resolved 422 response (`{ data }`) that the
 * response interceptor returns instead of throwing.
 *
 * @param {unknown} err
 * @param {string} [fallback="Something went wrong"]
 * @returns {string}
 */
export function getApiErrorMessage(err, fallback = "Something went wrong") {
  const data = err?.response?.data;
  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data?.error === "string" && data.error) return data.error;
  const fromErrors = firstErrorMessage(data?.errors);
  if (fromErrors) return fromErrors;
  if (typeof err?.data?.message === "string" && err.data.message) return err.data.message;
  if (
    typeof err?.message === "string" &&
    err.message &&
    !AXIOS_STATUS_MESSAGE.test(err.message) &&
    err.message !== "Network Error"
  ) {
    return err.message;
  }
  return fallback;
}

export default AtriumApi;